import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { AuthService } from '@app/services/auth/auth.service';
import {
  DELEGATED_CONSENT_USER_MISMATCH,
  DELEGATED_CONSENT_WRONG_TENANT
} from '@app/services/delegated-activation/delegated-activation.constants';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { SanitizationService } from '@app/services/sanitization/sanitization.service';

/**
 * The reasons this page can explain, by error code. Anything else falls back to the bare
 * "not completed" state — a code with no caption is better shown as nothing than as itself.
 *
 * A Map rather than an object literal, deliberately: the lookup key is a query parameter on an
 * unguarded route, so an object would resolve `?error_code=constructor` or `__proto__` off the
 * prototype chain and hand a non-string to the template. A Map has no prototype chain to walk.
 */
const FAILURE_MESSAGE_KEYS = new Map<string, string>([
  [DELEGATED_CONSENT_USER_MISMATCH, 'ConsentAccountMismatch'],
  [DELEGATED_CONSENT_WRONG_TENANT, 'ConsentWrongTenant']
]);

/**
 * Landing page the user is returned to after granting (or failing to grant) delegated
 * cloud-storage consent at Microsoft.
 *
 * Deliberately not behind authGuard: on native the user returns in the system browser,
 * which has no Printix session. The page reads only its own query string.
 *
 * Also reports the one failure the app detects *before* Microsoft is reached: a link minted for
 * a tenant this session is not signed in to, which ActivateDelegatedPage sends here rather than
 * calling an endpoint that can only refuse it.
 */
@Component({
  selector: 'app-consent-done',
  templateUrl: './consent-done.page.html',
  styleUrls: ['./consent-done.page.scss'],
  standalone: false
})
export class ConsentDonePage implements OnInit {

  public readonly succeeded = signal<boolean>(true);
  public readonly titleKey = signal<string>('ConsentGranted');
  public readonly messageKey = signal<string>('ConsentCloseWindow');

  // The reason behind a failure, kept because the consents link is wrong for one of them — see
  // showConsentsLink(). Empty on success, and on a failure carrying no error_code.
  private readonly failureCode = signal<string>('');

  private logger: Logger;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private loggingService: LoggingService,
    private navController: NavController,
    private pageTitleService: PageTitleService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = loggingService.getLogger('[ConsentDonePage]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  ngOnInit(): void {
    const methodName = 'ngOnInit()';
    this.logger.entry(methodName);
    // pageTitle is a shared signal; on native this is a cold load with nothing else setting it.
    this.pageTitleService.setTitle('');
    this.resolveOutcome();
  }

  // ############### OUTCOME ###############

  /**
   * Decides between the granted and not-completed states from the redirect's query string.
   *
   * A failure redirect carries `error`, `error_code` and `status` together, but any one of
   * them on its own is treated as a failure. The page defaults to success, so reading only
   * one of the three would show a green checkmark on a redirect that carried the other two —
   * the worst outcome this page can produce.
   */
  private resolveOutcome(): void {
    const methodName = 'resolveOutcome()';
    const params = this.activatedRoute.snapshot.queryParamMap;
    const error = params.get('error');
    const errorCode = params.get('error_code');
    const status = params.get('status');

    if (!this.indicatesFailure(error, errorCode, status)) {
      this.logger.info(methodName + ' consent granted');
      return;
    }

    this.succeeded.set(false);
    this.titleKey.set('ConsentNotCompleted');
    this.failureCode.set(errorCode ?? '');
    // Only a constant key is shown; the raw values are never rendered.
    this.messageKey.set(FAILURE_MESSAGE_KEYS.get(errorCode ?? '') ?? '');
    this.logger.info(methodName + ' consent not completed' +
      ', error: ' + this.sanitizationService.sanitizeLogOutput(error ?? '') +
      ', error_code: ' + this.sanitizationService.sanitizeLogOutput(errorCode ?? '') +
      ', status: ' + this.sanitizationService.sanitizeLogOutput(status ?? ''));
  }

  /**
   * True when the redirect carries any sign of failure.
   *
   * `status` is only conclusive when it parses to an HTTP error code — a success redirect may
   * legitimately carry status=200, so its mere presence means nothing. An unparseable status
   * is not treated as a failure on its own: a real failure carries `error` and `error_code`
   * alongside it.
   */
  private indicatesFailure(
    error: string | null,
    errorCode: string | null,
    status: string | null
  ): boolean {
    if (error || errorCode) {
      return true;
    }
    if (!status) {
      return false;
    }
    const statusCode = Number(status);
    return Number.isFinite(statusCode) && statusCode >= 400;
  }

  // ############### TEMPLATE HELPERS ###############

  /**
   * Only meaningful when this browser already holds a session; on native the user returns in the
   * system browser where nothing has verified credentials, so the link stays hidden.
   *
   * Hidden on the wrong-tenant outcome, where it would contradict the message: that text says the
   * request belongs to a different Printix home, and this link lists the consents of the home the
   * user is *currently* in. showExitLink() covers that case instead.
   */
  public showConsentsLink(): boolean {
    if (this.failureCode() === DELEGATED_CONSENT_WRONG_TENANT) {
      return false;
    }
    return this.authService.isAuthenticatedSignal();
  }

  /**
   * The way out of the wrong-tenant outcome, which is the only one reached by an in-app navigation
   * rather than as a browser landing.
   *
   * This page has no `<app-header>` and the side menu cannot be swiped open, and
   * ActivateDelegatedPage arrives here via navigateRoot, which discards the back stack. So with no
   * button at all the user is stranded — on Cordova there is no URL bar or browser back either, and
   * the app has to be killed. Every other activation failure lands on /print-jobs, which has a
   * header; this sends them to the same place deliberately rather than leaving them nowhere.
   */
  public showExitLink(): boolean {
    return this.failureCode() === DELEGATED_CONSENT_WRONG_TENANT
      && this.authService.isAuthenticatedSignal();
  }

  public goToPrintJobs(): void {
    this.navController.navigateRoot(['/print-jobs']);
  }

  public goToConsents(): void {
    this.navController.navigateRoot(['/go']);
  }

}
